import { IPromise } from 'angular'
import ngRedux from 'ng-redux'
import { ApiCustomer, ApiProduct } from '@vend/types'
import {
  CustomFieldsWorkflowsManager,
  WorkflowsAddLineItemAction,
  WorkflowsCustomFieldDefinition,
  WorkflowsCustomFieldEntityType,
  WorkflowsCustomFieldValue,
  WorkflowsDialogAnchor,
  WorkflowsEvent,
  WorkflowsEventOptions,
  WorkflowsManager,
  WorkflowsOutcome,
  WorkflowsPayload,
  WorkflowsPayloadCustomField,
  WorkflowsRemoveLineItemAction,
  WorkflowsSetCustomerAction,
  WorkflowsSetCustomFieldAction,
  WorkflowsSetLineItemTaxAction,
} from '@vend/business-components'
import { RegisterManager } from '../../managers/register-manager'
import { client } from '../../apollo'
import { LineItem } from '../../models/line-item'
import {
  CustomFieldValue,
  getCustomField,
  getImageURI,
  setCustomField,
} from '../../models/custom-fields'
import { ProductsStore as ProductStore } from '../../factories/database/products-store'
import { $log, $rootScope, $state } from '../../ng-compat/imports'
import { SaleManager } from '../../managers/sale-manager'
import { EVENT } from '../../constants'
import { CustomersStore } from '../../factories/database/customers-store'
import { STATES } from '../../../../lib/routes'
import {
  ReduxCurrentSale,
  customFieldsSet,
  selectCurrentSale,
  updateLineItem,
} from '../../state/slices/currentSale/currentSaleSlice'
import { lineItemsAdapter } from '../../state/slices/currentSale/lineItemEntityAdapter'
import type { ProductSkuIndex } from '../../factories/search/product-sku-index'

export type EventType =
  | 'sale.ready_for_payment'
  | 'sale.line_items.added'
  | 'sale.line_items.updated'
  | 'sale.line_items.deleted'
  | 'sale.customer.changed'

export interface WorkflowPayloadCustomer {
  id: string
  first_name: string | null
  last_name: string | null
  customer_code: string | null
  customer_group_id: string | null
  email: string | null
  phone: string | null
  mobile: string | null
  fax: string | null
  do_not_email: boolean
  note: string | null
  date_of_birth: string | null
  year_to_date: string
  balance: string
  loyalty_balance: string
  created_at: string
  enable_loyalty: boolean
  custom_field_1: string | null
  custom_field_2: string | null
  custom_field_3: string | null
  custom_field_4: string | null
}

export interface WorkflowTaxComponent {
  rate_id: string
  tax_total: string
}

export interface WorkflowPayloadLineItem {
  id: string
  product_id: string
  quantity: string
  price: string
  base_price: string
  price_total: string
  tax_total: string
  note: string | null
  tax_id: string
  tax_components: WorkflowTaxComponent[]
  custom_fields: WorkflowsPayloadCustomField[]
}

export interface WorkflowPayloadSale {
  id?: string
  status: string
  outlet_id: string
  register_id: string
  return_for: string | null
  note: string | null
  total_price: string
  total_tax: string
  custom_fields: WorkflowsPayloadCustomField[]
  line_items: WorkflowPayloadLineItem[]
}

export interface RegisterWorkflowsPayload extends WorkflowsPayload {
  sale: WorkflowPayloadSale
  customer: WorkflowPayloadCustomer | null
  line_items?: WorkflowPayloadLineItem[]
  removed_line_item_ids?: string[]
}

interface RegisterWorkflowsEventOptions
  extends WorkflowsEventOptions<EventType> {
  lineItemsAdded?: string[]
  lineItemsUpdated?: string[]
  lineItemsRemoved?: string[]
}

export class RegisterWorkflowsEvent extends WorkflowsEvent<EventType> {
  public lineItemsAdded: string[] = []
  public lineItemsUpdated: string[] = []
  public lineItemsRemoved: string[] = []

  constructor({
    lineItemsAdded = [],
    lineItemsUpdated = [],
    lineItemsRemoved = [],
    type,
    ...options
  }: RegisterWorkflowsEventOptions) {
    super({ type, ...options })
    this.type = type
    this.lineItemsAdded = lineItemsAdded
    this.lineItemsUpdated = lineItemsUpdated
    this.lineItemsRemoved = lineItemsRemoved
  }

  public lineItemAdded(id: string) {
    if (this.lineItemsAdded.includes(id)) return
    this.lineItemsAdded.push(id)
  }

  public lineItemUpdated(id: string) {
    if (this.lineItemsUpdated.includes(id)) return
    this.lineItemsUpdated.push(id)
  }

  public lineItemRemoved(id: string) {
    const index = this.lineItemsRemoved.indexOf(id)
    if (index === -1) return
    this.lineItemsRemoved.splice(index, 1)
  }
}

export class WorkflowService
  extends WorkflowsManager<RegisterWorkflowsEvent, RegisterWorkflowsPayload>
  implements CustomFieldsWorkflowsManager {
  private whenInitialised?: IPromise<void>

  public currentSale: ReduxCurrentSale

  constructor(
    private readonly registerManager: RegisterManager,
    private readonly saleManager: SaleManager,
    private readonly productsStore: ProductStore,
    private readonly productSkuIndex: ProductSkuIndex,
    private customersStore: CustomersStore,
    private readonly $ngRedux: ngRedux.INgRedux
  ) {
    'ngInject'

    super({
      apolloClient: client,
      payload: event => {
        const sale = this.getSale()
        let line_items: WorkflowPayloadLineItem[] = []
        let removed_line_item_ids: string[] = []
        if (event.type === `sale.line_items.added`) {
          line_items = sale.line_items.filter(li =>
            event.lineItemsAdded.includes(li.id)
          )
        }

        if (event.type === `sale.line_items.updated`) {
          line_items = sale.line_items.filter(li =>
            event.lineItemsUpdated.includes(li.id)
          )
        }

        if (
          event.type === 'sale.line_items.deleted' &&
          event.lineItemsRemoved.length > 0
        ) {
          removed_line_item_ids = event.lineItemsRemoved
        }

        return {
          sale,
          line_items,
          removed_line_item_ids,
          customer: this.getCustomer(),
        }
      },
    })

    this.currentSale = selectCurrentSale($ngRedux.getState())
    const disconect = $ngRedux.connect(state => ({
      currentSale: selectCurrentSale(state),
    }))(this)

    $rootScope.$on('$destroy', disconect)
  }

  public init() {
    if (!this.whenInitialised) {
      this.saleManager.on(EVENT.SALE_ADD_LINE_ITEM, ({ lineItem, context }) => {
        const lineItemId = lineItem.id || lineItem.localId
        const element = document.getElementById(
          `wr-sale-line-item-${lineItemId}`
        )
        const anchoredTo: WorkflowsDialogAnchor | null = element
          ? { element, placement: 'left' }
          : null
        return this.lineItemAdded(lineItemId, anchoredTo, context?.workflow)
      })

      $rootScope.$on(
        'sale:update-line-item',
        (
          event: any,
          lineItem: { id: string; localId: string },
          context?: {
            workflow?: RegisterWorkflowsEvent
          }
        ) => {
          const lineItemId = lineItem.id || lineItem.localId
          const element = document.getElementById(
            `wr-sale-line-item-${lineItemId}`
          )
          const anchoredTo: WorkflowsDialogAnchor | null = element
            ? { element, placement: 'left' }
            : null
          return this.lineItemUpdated(lineItemId, anchoredTo, context?.workflow)
        }
      )

      $rootScope.$on(
        'sale:remove-line-item',
        (
          event: any,
          lineItem: { id: string; localId: string },
          context?: {
            workflow?: RegisterWorkflowsEvent
          }
        ) => {
          const lineItemId = lineItem.id || lineItem.localId
          const element = document.getElementById(
            `wr-sale-line-item-${lineItemId}`
          )
          const anchoredTo: WorkflowsDialogAnchor | null = element
            ? { element, placement: 'left' }
            : null
          return this.lineItemRemoved(lineItemId, anchoredTo, context?.workflow)
        }
      )

      $rootScope.$on(
        EVENT.SALE_CUSTOMER_UPDATED,
        (
          event: any,
          context?: {
            workflow?: RegisterWorkflowsEvent
          }
        ) => {
          let element = document.getElementById('wr-sale-header')
          let placement: 'top' | 'right' | 'bottom' | 'left' = 'bottom'

          if (element === null) {
            element = document.querySelector(
              '#wr-payment-add-customer .wr-customer-badge-content'
            )
            placement = 'left'
          }

          if (element === null) {
            element = document.getElementById('wr-payment-add-customer')
            placement = 'left'
          }

          const anchoredTo: WorkflowsDialogAnchor | null = element
            ? { element, placement }
            : null

          return this.customerChanged(anchoredTo, context?.workflow)
        }
      )
    }

    return this.whenInitialised
  }

  public handleSetCustomFieldAction(
    event: RegisterWorkflowsEvent,
    action: WorkflowsSetCustomFieldAction
  ): IPromise<WorkflowsOutcome> {
    const { custom_field: customField } = action

    if (!customField || action.custom_field_value === undefined) {
      return Promise.resolve({ proceed: true, modified: false })
    }

    return this.setCustomFieldValue(
      customField,
      action.entity_id || null,
      action.custom_field_value
    ).then(modified => {
      return { proceed: true, modified }
    })
  }

  public handleAddLineItemAction(
    event: RegisterWorkflowsEvent,
    action: WorkflowsAddLineItemAction
  ): IPromise<WorkflowsOutcome> {
    return this.addLineItem(
      { id: action.product_id, sku: action.product_sku },
      {
        quantity: action.quantity || 1,
        price: action.unit_price,
        note: action.note,
      },
      event
    ).then(modified => {
      return { proceed: true, modified }
    })
  }

  public handleRemoveLineItemAction(
    event: RegisterWorkflowsEvent,
    action: WorkflowsRemoveLineItemAction
  ): IPromise<WorkflowsOutcome> {
    event.lineItemRemoved(action.line_item_id)

    const lineItem = this.findLineItem(action.line_item_id)
    if (lineItem) {
      return this.saleManager
        .removeLineItem(lineItem)
        .then(() => {
          return { proceed: true, modified: true }
        })
        .catch((reason: Error) => {
          $log.warn('[Workflows] Failed to remove line item', reason)
          return { proceed: true, modified: false }
        })
    } else {
      return Promise.resolve({ proceed: true, modified: false })
    }
  }

  public handleSetCustomerAction(
    event: RegisterWorkflowsEvent,
    action: WorkflowsSetCustomerAction
  ): IPromise<WorkflowsOutcome> {
    if (!action.customer_id) {
      // The action is to unset the customer.
      if (!this.currentSale.customer) {
        // The sale already has no customer. Nothing to do.
        return Promise.resolve({ proceed: true, modified: false })
      }

      return this.saleManager.removeCustomer().then(() => {
        return { proceed: true, modified: true }
      })
    }

    if (
      this.currentSale.customer &&
      this.currentSale.customer.id === action.customer_id
    ) {
      // The new customer id is the same as the existing custumer id. Nothing to do.
      return Promise.resolve({ proceed: true, modified: false })
    }

    return this.customersStore
      .get(action.customer_id)
      .then((customer?: ApiCustomer) => {
        if (!customer) {
          $log.warn(
            '[Workflows] Could not find customer with ID',
            action.customer_id
          )
          return { proceed: true, modified: false }
        }

        return this.saleManager.addCustomer(customer).then(() => {
          return { proceed: true, modified: true }
        })
      })
  }

  public handleSetLineItemTaxAction(
    event: RegisterWorkflowsEvent,
    action: WorkflowsSetLineItemTaxAction
  ): IPromise<WorkflowsOutcome> {
    if (!action.tax_id || !action.line_item_id) {
      return Promise.resolve({ proceed: true, modified: false })
    }

    const lineItem = this.findLineItem(action.line_item_id)
    if (!lineItem) {
      return Promise.resolve({ proceed: true, modified: false })
    }

    if (lineItem.taxInfo?.id === action.tax_id) {
      return Promise.resolve({ proceed: true, modified: false })
    }

    return this.saleManager
      .setTaxOnLineItem(lineItem, action.tax_id)
      .then(() => {
        return { proceed: true, modified: true }
      })
  }

  private lookupProduct(product: {
    id?: string
    sku?: string
  }): IPromise<ApiProduct> {
    return new Promise<ApiProduct>((resolve, reject) => {
      if (product.id) {
        this.productsStore
          .get(product.id)
          .then(p => {
            p
              ? resolve(p)
              : reject(
                  new Error(`There are no products with the ID ${product.id}.`)
                )
          })
          .catch(reason => reject(reason))
      } else if (product.sku) {
        this.productSkuIndex
          .search(product.sku)
          .then((results: any) => {
            if (!results || !results.length) {
              return reject(
                new Error(`There are no products with the SKU ${product.sku}.`)
              )
            }

            if (results.length > 1) {
              return reject(
                new Error(
                  `There are multiple products with SKU ${product.sku}.`
                )
              )
            }

            if (!results[0].exactMatch || results[0].type !== 'product') {
              return reject(
                new Error(`There are no products with the SKU ${product.sku}.`)
              )
            }

            return resolve(results[0].product)
          })
          .catch((reason: any) => reject(reason))
      } else {
        reject(new Error('You must provide a product_id or product_sku.'))
      }
    })
  }

  public addLineItem(
    product: {
      id?: string
      sku?: string
    },
    options: {
      quantity: number | string
      price?: string
      note?: string
    },
    event?: RegisterWorkflowsEvent
  ): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      return this.lookupProduct(product)
        .then(p => {
          return this.saleManager.addProductToSale(p, options, {
            workflow: event,
          })
        })
        .then(() => {
          resolve(true)
        })
        .catch((reason: Error) => {
          $log.warn('[Workflows] Failed to add line item', reason)
          resolve(false)
        })
    })
  }

  /**
   * Sets the custom field value on the sale or line item.
   * @todo This function has changed from its original logic during the
   * migration of state to Redux and is doing a lot. It should be refactored
   * to its original shape when we use Redux.
   */
  public setCustomFieldValue(
    field: WorkflowsCustomFieldDefinition,
    entityId: string | null,
    value: WorkflowsCustomFieldValue
  ): Promise<boolean> {
    const entity = this.getCustomFieldEntity(
      field.entity,
      entityId || undefined
    )

    if (!entity) {
      return Promise.resolve(false)
    }

    const customFieldValue: CustomFieldValue = {
      definitionId: field.id,
      name: field.name,
      title: field.title,
      type: field.type,
      printOnReceipt: field.print_on_receipt,
      visibleInUI: field.visible_in_ui,
      value,
    }

    const entry = getCustomField(customFieldValue.name, entity.customFields)
    if (entry && entry.value === customFieldValue.value) {
      return Promise.resolve(false)
    }

    if (field.type !== 'qrcode') {
      const updatedCustomFields = setCustomField(
        customFieldValue,
        entity.customFields
      )

      this.setCustomFieldValuesOnEntity(entity, updatedCustomFields)

      return Promise.resolve(true)
    } else {
      return getImageURI(customFieldValue).then(imageURI => {
        // There is a lag in when the parent function is called to set the QR
        // code and when this callback is executed. This will cause the entity
        // to have reference to the old values so fetching the entity again.
        const updatedEntity = this.getCustomFieldEntity(
          field.entity,
          entityId || undefined
        )

        if (!updatedEntity) {
          return false
        }

        customFieldValue.imageURI = imageURI

        const updatedCustomFields = setCustomField(
          customFieldValue,
          updatedEntity.customFields
        )

        this.setCustomFieldValuesOnEntity(updatedEntity, updatedCustomFields)

        return true
      })
    }
  }

  /**
   * Sets the customFiels on given entity.
   * Lineitems are serialised objects and Sale is still a class and are set/
   * updated differently on these entities.
   *
   * @param entity
   * @param customFields
   */
  private setCustomFieldValuesOnEntity(
    entity: ReduxCurrentSale | LineItem,
    customFields: CustomFieldValue[]
  ) {
    if (this.isReduxCurrentSale(entity)) {
      this.$ngRedux.dispatch(
        customFieldsSet({
          customFields,
          hasUpdatedCustomFieldsOnConfirmation:
            $state.current.name === STATES.SALE_CONFIRMATION &&
            this.currentSale.isSubmittedSaleEarly,
        })
      )
    } else {
      this.$ngRedux.dispatch(updateLineItem({ ...entity, customFields }))
    }
  }

  private isReduxCurrentSale(
    entity: ReduxCurrentSale | LineItem
  ): entity is ReduxCurrentSale {
    return entity != null && 'lineItems' in entity
  }

  public getCustomFieldValues(
    entityType: WorkflowsCustomFieldEntityType,
    entityId?: string
  ):
    | Array<{
        name: string
        value: WorkflowsCustomFieldValue
      }>
    | undefined {
    const entity = this.getCustomFieldEntity(entityType, entityId || undefined)

    if (!entity) {
      return undefined
    }

    return entity.customFields
  }

  /**
   * Get the current sale in the format required by the Workflow API
   *
   * @method getSale
   */
  private getSale(): WorkflowPayloadSale {
    const sale: ReduxCurrentSale = this.currentSale
    return {
      id: sale.id || sale.localId,
      status: sale.status || 'OPEN',
      outlet_id: this.registerManager.getActiveOutlet().id,
      register_id: this.registerManager.getActiveRegister().id,
      return_for: sale.returnFor,
      note: sale.note,
      total_price: sale.totals.exclusivePrice.toString(),
      total_tax: sale.totals.tax.toString(),
      custom_fields: sale.customFields.map(customField => {
        return {
          definition_id: customField.definitionId,
          name: customField.name,
          type: customField.type,
          value: customField.value,
        }
      }),
      line_items: lineItemsAdapter
        .getSelectors()
        .selectAll(sale.lineItems)
        .map(this.lineItemForWorkflows),
    }
  }

  private lineItemForWorkflows(lineItem: LineItem): WorkflowPayloadLineItem {
    return {
      id: lineItem.id || lineItem.localId,
      product_id: lineItem.product.id,
      quantity: lineItem.quantity.toString(),
      price: lineItem.price.toString(),
      base_price: lineItem.displayBasePrice.toString(),
      price_total: lineItem.totals.exclusivePrice.toString(),
      tax_total: lineItem.totals.tax.toString(),
      note: lineItem.note,
      tax_id: lineItem.taxInfo!.id,
      tax_components: lineItem.taxComponents.map(tc => ({
        rate_id: tc.rate_id,
        tax_total: tc.total_tax.toString(),
      })),
      custom_fields: lineItem.customFields.map(customField => {
        return {
          definition_id: customField.definitionId,
          name: customField.name,
          type: customField.type,
          value: customField.value,
        }
      }),
    }
  }

  /**
   * Get the current customer (if any) in the format required by the Workflow API
   *
   * @method getCustomer
   */
  private getCustomer(): WorkflowPayloadCustomer | null {
    const { customer } = this.currentSale
    if (!customer) {
      return null
    }

    return {
      id: customer.id,
      first_name: customer.first_name,
      last_name: customer.last_name,
      customer_code: customer.customer_code,
      customer_group_id: customer.customer_group_id,
      email: customer.email,
      phone: customer.phone,
      mobile: customer.mobile,
      fax: customer.fax,
      do_not_email: customer.do_not_email,
      note: customer.note,
      date_of_birth: customer.date_of_birth,
      year_to_date: customer.year_to_date.toString(),
      balance: customer.balance.toString(),
      loyalty_balance: customer.loyalty_balance.toString(),
      created_at: customer.created_at,
      enable_loyalty: customer.enable_loyalty,
      custom_field_1: customer.custom_field_1,
      custom_field_2: customer.custom_field_2,
      custom_field_3: customer.custom_field_3,
      custom_field_4: customer.custom_field_4,
    }
  }

  /**
   * Look up an entity for setting custom fields on.
   *
   * @method getCustomFieldEntity
   *
   * @param type the type of entuty to look up
   * @param id the id of the entity to look up
   *
   * @returns {CustomFieldEntity} or null if the entity can not be found
   */
  public getCustomFieldEntity(
    type: WorkflowsCustomFieldEntityType,
    id?: string
  ): ReduxCurrentSale | LineItem | null | undefined {
    switch (type) {
      case 'sale':
        return this.currentSale
      case 'line_item':
        return this.findLineItem(id)
      default:
        $log.warn(
          '[Workflows] Unsupported entity in requre_custom_field action',
          { type, id }
        )
        return null
    }
  }

  public findLineItem(lineItemId?: string): LineItem | null | undefined {
    if (!lineItemId) {
      return null
    }
    return lineItemsAdapter
      .getSelectors()
      .selectAll(this.currentSale.lineItems)
      .find(
        lineItem =>
          lineItem?.localId === lineItemId || lineItem?.id === lineItemId
      )
  }

  public readyForPayment(
    anchoredTo: WorkflowsDialogAnchor | null
  ): Promise<boolean> {
    return this.trigger(
      new RegisterWorkflowsEvent({
        type: 'sale.ready_for_payment',
        anchoredTo,
      })
    )
  }

  public async lineItemAdded(
    lineItemId: string,
    anchoredTo: WorkflowsDialogAnchor | null,
    event?: RegisterWorkflowsEvent
  ): Promise<void> {
    if (!event) {
      await this.trigger(
        new RegisterWorkflowsEvent({
          type: 'sale.line_items.added',
          lineItemsAdded: [lineItemId],
          anchoredTo,
        })
      )

      return
    }

    if (event.type === 'sale.line_items.added') {
      // We are currently in the context of a sale.line_items.added event.
      event.lineItemAdded(lineItemId)
    } else {
      // We are in the context of a non-sale.line_items.added event.
      const lineItemsAddedEvent = this.eventQueue.find(
        e => e.type === 'sale.line_items.added'
      )
      if (lineItemsAddedEvent) {
        // There is a sale.line_items.added event on the queue, let's add to that.
        event.lineItemAdded(lineItemId)
      } else {
        // We enqueue a sale.line_items.added event.
        this.eventQueue.push(
          new RegisterWorkflowsEvent({
            type: 'sale.line_items.added',
            lineItemsAdded: [lineItemId],
            anchoredTo,
          })
        )
      }
    }
  }

  public async lineItemUpdated(
    lineItemId: string,
    anchoredTo: WorkflowsDialogAnchor | null,
    event?: RegisterWorkflowsEvent
  ): Promise<void> {
    if (!event) {
      await this.trigger(
        new RegisterWorkflowsEvent({
          type: 'sale.line_items.updated',
          lineItemsUpdated: [lineItemId],
          anchoredTo,
        })
      )

      return
    }

    if (event.type === 'sale.line_items.updated') {
      // We are currently in the context of a sale.line_items.updated event.
      event.lineItemUpdated(lineItemId)
    } else {
      // We are in the context of a non-sale.line_items.updated event.
      const lineItemsUpdatedEvent = this.eventQueue.find(
        e => e.type === 'sale.line_items.updated'
      )
      if (lineItemsUpdatedEvent) {
        // There is a sale.line_items.updated event on the queue, let's add to that.
        event.lineItemUpdated(lineItemId)
      } else {
        // We enqueue a sale.line_items.updated event.
        this.eventQueue.push(
          new RegisterWorkflowsEvent({
            type: 'sale.line_items.updated',
            lineItemsUpdated: [lineItemId],
            anchoredTo,
          })
        )
      }
    }
  }

  public async lineItemRemoved(
    lineItemId: string,
    anchoredTo: WorkflowsDialogAnchor | null,
    event?: RegisterWorkflowsEvent
  ): Promise<void> {
    if (!event) {
      await this.trigger(
        new RegisterWorkflowsEvent({
          type: 'sale.line_items.deleted',
          lineItemsRemoved: [lineItemId],
          anchoredTo,
        })
      )

      return
    }

    if (event.type === 'sale.line_items.deleted') {
      // We are currently in the context of a sale.line_items.deleted event.
      event.lineItemRemoved(lineItemId)
    } else {
      // We are in the context of a non-sale.line_items.deleted event.
      const lineItemsRemovedEvent = this.eventQueue.find(
        e => e.type === 'sale.line_items.deleted'
      )
      if (lineItemsRemovedEvent) {
        // There is a sale.line_items.deleted event on the queue, let's add to that.
        event.lineItemRemoved(lineItemId)
      } else {
        // We enqueue a sale.line_items.deleted event.
        this.eventQueue.push(
          new RegisterWorkflowsEvent({
            type: 'sale.line_items.deleted',
            lineItemsRemoved: [lineItemId],
            anchoredTo,
          })
        )
      }
    }
  }

  public async customerChanged(
    anchoredTo: WorkflowsDialogAnchor | null,
    event?: RegisterWorkflowsEvent
  ): Promise<void> {
    if (event) {
      // The customer changed during an event.

      if (!this.eventQueue.find(e => e.type === 'sale.customer.changed')) {
        // There is not already a sale.customer.changed event on the queue.
        this.eventQueue.push(
          new RegisterWorkflowsEvent({
            type: 'sale.customer.changed',
            anchoredTo,
          })
        )
      }
      return Promise.resolve(undefined)
    }

    await this.trigger(
      new RegisterWorkflowsEvent({
        type: 'sale.customer.changed',
        anchoredTo,
      })
    )
  }
}
